import { Component, OnInit } from '@angular/core';
import { SessionService } from 'src/app/shared/services/session.service';
import { BookingService } from '../services/booking.service';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';

@Component({
    selector: 'simbook-dashboard',
    templateUrl: './simbook-dashboard.component.html',
    styleUrl: './simbook-dashboard.component.scss',
    standalone: false
})
export class SimbookDashboardComponent extends BaseComponent implements OnInit {
  dashboardData: any = {};

  weeklyChartData: any = {
    labels: [],
    datasets: [{
      label: 'Number of Bookings',
      data: [],
      backgroundColor: 'rgba(66, 165, 245, 0.1)',
      borderColor: 'rgba(66, 165, 245, 1)'
    }]
  };

  monthlyChartData: any = {
    labels: [],
    datasets: [{
      label: 'Number of Bookings',
      data: [],
      backgroundColor: 'rgba(66, 165, 245, 0.1)',
      borderColor: 'rgba(66, 165, 245, 1)'
    }]
  };

  chartOptions: any = {
    plugins: {
      legend: {
        display: true
      }
    },
    scales: {
      x: {
        stacked: true
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  currentMonth: string = '';
  currentWeek: { startDate: string; endDate: string } = { startDate: '', endDate: '' };
  isDarkTheme: boolean = false;

  constructor(sessionService: SessionService,
    stringHelpers: StringHelpers,
    private bookingService: BookingService,
    private globalDataService: GlobalDataService
  ) {
    super(sessionService, stringHelpers);
    this.getDashboardData();
    this.isDarkTheme = this.sessionService.getUser().defaultTheme;
  }

  ngOnInit(): void {
    this.currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' });
    this.setCurrentWeek();
    
    this.globalDataService.themeChangeEvent.subscribe((isDarkTheme: boolean) => {
      this.isDarkTheme = isDarkTheme;
    });
  }

  private setCurrentWeek() {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - now.getDay()); // Get Sunday
    
    const endDate = new Date(now);
    endDate.setDate(now.getDate() + (6 - now.getDay())); // Get Saturday
    
    this.currentWeek = {
      startDate: startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      endDate: endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  }

  getDashboardData() {
    this.isLoading = true;
    this.bookingService.getDashboard(this.sessionService.getUser().organizationId).subscribe({
      next: (data) => {
        this.dashboardData.data = data;
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.updateCharts();
      }
    });
  }

  private updateCharts() {
    if (!this.dashboardData.data) return;

    // Weekly chart
    const thisWeek = new Date();
    const weekLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const bookedData = Array(7).fill(0);
    const cancelledData = Array(7).fill(0);

    this.dashboardData.data.forEach(booking => {
      const bookingDate = new Date(booking.startTime);
      const dayOfWeek = bookingDate.getDay();
      const bookingWeekStart = new Date(bookingDate);
      bookingWeekStart.setDate(bookingDate.getDate() - bookingWeekStart.getDay());

      // Only count if booking is in current week
      if (bookingWeekStart.toDateString() === new Date(thisWeek.setDate(thisWeek.getDate() - thisWeek.getDay())).toDateString()) {
        if (booking.status === 1) {
          bookedData[dayOfWeek]++;
        } else if (booking.status === 2) {
          cancelledData[dayOfWeek]++;
        }
      }
    });

    this.weeklyChartData = {
      labels: weekLabels,
      datasets: [
        {
          label: 'Booked',
          data: bookedData,
          backgroundColor: '#42A5F5'
        },
        {
          label: 'Cancelled',
          data: cancelledData,
          backgroundColor: '#dc3545'
        }
      ]
    };

    // Monthly chart
    const thisMonth = new Date();
    const daysInMonth = new Date(thisMonth.getFullYear(), thisMonth.getMonth() + 1, 0).getDate();
    const monthLabels = Array.from({length: daysInMonth}, (_, i) => i + 1);

    const monthlyBookedData = monthLabels.map(day => {
      const d = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), day);
      return this.dashboardData.data.filter(x => 
        new Date(x.startTime).getDate() === d.getDate() && 
        new Date(x.startTime).getMonth() === d.getMonth() &&
        x.status === 1
      ).length;
    });

    const monthlyCancelledData = monthLabels.map(day => {
      const d = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), day);
      return this.dashboardData.data.filter(x => 
        new Date(x.startTime).getDate() === d.getDate() && 
        new Date(x.startTime).getMonth() === d.getMonth() &&
        x.status === 2
      ).length;
    });

    this.monthlyChartData = {
      labels: monthLabels,
      datasets: [
        {
          label: 'Booked',
          data: monthlyBookedData,
          backgroundColor: '#42A5F5',
          stack: 'stack1'
        },
        {
          label: 'Cancelled',
          data: monthlyCancelledData,
          backgroundColor: '#dc3545',
          stack: 'stack1'
        }
      ]
    };
  }

  get recentActivity(): any {
    let data: any = [];
    if (this.dashboardData.data && this.dashboardData.data.length > 0) {
      const sortedData = [...this.dashboardData.data].sort((a, b) => 
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      );

      const maxItems = Math.min(3, sortedData.length);
      for (let i = 0; i < maxItems; i++) {
        data.push({
          name: sortedData[i].customerName,
          startTime: sortedData[i].startTime,
          endTime: sortedData[i].endTime,
          confirmationNumber: sortedData[i].confirmationNumber,
          status: sortedData[i].status === 1 ? 'Booked' : 'Cancelled'
        });
      }
    }
    return data;
  }

  get overview(): any {
    let data: any = {};
    data.booked = this.dashboardData.data?.filter(x => x.status === 1).length || 0;
    data.cancelled = this.dashboardData.data?.filter(x => x.status === 2).length || 0;
    data.revenue = this.dashboardData.data?.filter(x => x.hasPaid === true).reduce((acc, curr) => acc + curr.totalPrice, 0) || 0;
    return data;
  }

  get todaysRevenue(): number {
    const today = new Date();
    return this.dashboardData.data?.filter(x => 
      new Date(x.startTime).getDate() === today.getDate() && 
      new Date(x.startTime).getMonth() === today.getMonth() &&
      new Date(x.startTime).getFullYear() === today.getFullYear() &&
      x.status === 1 && 
      (x.refundAmount === null || x.refundAmount === undefined)
    ).reduce((acc, curr) => acc + curr.totalPrice, 0) || 0;
  }

  get todaysBookings(): number {
    const today = new Date();
    return this.dashboardData.data?.filter(x => 
      new Date(x.startTime).getDate() === today.getDate() &&
      new Date(x.startTime).getMonth() === today.getMonth() &&
      new Date(x.startTime).getFullYear() === today.getFullYear() &&
      x.status === 1
    )?.length || 0;
  }

  get paidBookings(): number {
    const today = new Date();
    return this.dashboardData.data?.filter(x => 
      x.hasPaid === true && 
      new Date(x.startTime).getDate() === today.getDate() &&
      new Date(x.startTime).getMonth() === today.getMonth() &&
      new Date(x.startTime).getFullYear() === today.getFullYear() &&
      x.status === 1
    )?.length || 0;
  }

  get unpaidBookings(): number {
    const today = new Date();
    return this.dashboardData.data?.filter(x => 
      x.hasPaid === false && 
      new Date(x.startTime).getDate() === today.getDate() &&
      new Date(x.startTime).getMonth() === today.getMonth() &&
      new Date(x.startTime).getFullYear() === today.getFullYear() &&
      x.status === 1
    )?.length || 0;
  }

  get todaysBookingHours(): number {
    if (!this.dashboardData.data) return 0;
    
    const today = new Date();
    return this.dashboardData.data
      .filter(x => 
        new Date(x.startTime).getDate() === today.getDate() &&
        new Date(x.startTime).getMonth() === today.getMonth() &&
        new Date(x.startTime).getFullYear() === today.getFullYear() &&
        x.status === 1
      )
      .reduce((total, booking) => {
        const start = new Date(booking.startTime);
        const end = new Date(booking.endTime);
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return total + hours;
      }, 0);
  }
}
